import * as React from 'react';
import { makeStyles, Theme, createStyles, Typography, Tooltip } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import GavelIcon from '@material-ui/icons/Gavel';
import EventIcon from '@material-ui/icons/Event';
import { SkeletonLoader } from '../../../components/loading/SkeletonLoader';
import { RefreshingIndicator } from './RefreshingIndicator';
import {
    MOBILE_ACCENT_TEAL,
    MOBILE_ACCENT_TEAL_SOFT,
    MOBILE_STATUS_PENDING,
    MOBILE_STATUS_PENDING_SOFT,
    MOBILE_STATUS_URGENT,
    MOBILE_STATUS_URGENT_SOFT,
    MOBILE_STATUS_GREEN,
    MOBILE_STATUS_GREEN_SOFT,
    MOBILE_SURFACE_MUTED,
    MOBILE_TEXT_MUTED,
} from './YourEnquiriesWidget';

// A brighter, lighter red than the app's standard error tone (--dc-error-fg #a32d2d on
// --dc-error-bg #fcebeb) - explicit request, that pairing "looked bad" here. Matches
// MOBILE_STATUS_URGENT/_SOFT already used a few files over in YourEnquiriesWidget.tsx/
// YourTasksWidget.tsx, so the one red accent visible in this drawer is consistent everywhere
// it appears rather than two different reds.
const OVERDUE_RED = '#e5484d';
const OVERDUE_RED_SOFT = '#fde8e8';

// This app's own brand blue (theme.palette.primary.main / mainTheme.TemplateColor.Primary,
// src/index.tsx) - the same shared accent YourEnquiriesWidget.tsx/YourTasksWidget.tsx/
// KpiSummaryWidget.tsx use for their own wash/watermark. Explicit request: "I want the same
// background color as your enquiries in all widgets. don't use different colours" - the wash/
// watermark below was previously tinted per call site (props.iconColor, a different blue/
// teal/pink per widget), which is the icon CHIP's own long-standing category-accent colour
// (App.css's --dc-access-fg/--dc-content-fg/--dc-comms-fg pairs) and stays that way - it's
// only the wash/watermark that now matches every other widget's shared blue instead.
const SHARED_WASH_ACCENT = '#5173FF';

// Rows past this count sit below the fold of the bounded list box below and only reveal
// themselves by scrolling within it - keeps the drawer's own outer scroll from having to
// carry every widget's entire un-paginated row count at once. ROW_HEIGHT_PX is an estimate
// (row padding + a two-line row's own content + the gap between rows) used only to size that
// box - real row heights still vary a little by content, so the box shows "about" this many.
const VISIBLE_ROWS_BEFORE_SCROLL = 4;
const ROW_HEIGHT_PX = 64;
// A "card" row (avatar + name + subtitle + status/category + footer meta line) needs
// noticeably more vertical room than a compact two-line row - same estimate-for-sizing-only
// reasoning as ROW_HEIGHT_PX above.
const CARD_ROW_HEIGHT_PX = 84;
// How close to the bottom of the list box (in px) triggers the next page.
const LOAD_MORE_THRESHOLD_PX = 48;

// Explicit request: match the mobile app's own enquiry list card look (see EnquiryFollowUpsDueWidget.tsx's
// own comment for the reference), not this drawer's older board-card status palette, which
// read as dated. The status vocabulary is server-driven (no fixed enum) - same reasoning as
// BoardCardDraggable.tsx's own getStatusBucket - so unrecognised strings fall back to
// 'other' rather than guessing. Assigned/Following Up/Pending Matter Create used to share one
// merged "inProgress" bucket - split apart per explicit per-status colour requests (green for
// Assigned, red for Following Up, blue for Pending Matter Create), each now its own bucket.
type CardStatusBucket = 'pending' | 'open' | 'assigned' | 'followingUp' | 'pendingMatterCreate' | 'converted' | 'lost' | 'unassigned' | 'other';

const getCardStatusBucket = (status: string | null | undefined): CardStatusBucket => {
    const lower = (status ?? '').toLowerCase();
    if (lower === 'lost') { return 'lost'; }
    if (lower === 'client created' || lower === 'matter created' || lower === 'client already exists') { return 'converted'; }
    if (lower === 'unassigned') { return 'unassigned'; }
    if (lower === 'pending') { return 'pending'; }
    if (lower === 'open') { return 'open'; }
    if (lower === 'assigned') { return 'assigned'; }
    if (lower === 'following up') { return 'followingUp'; }
    if (lower === 'pending matter' || lower === 'pending matter creation' || lower === 'pending matter create') { return 'pendingMatterCreate'; }
    return 'other';
};

// Explicit request: match Your Personal Enquiries widget's own palette (YourEnquiriesWidget.tsx,
// exported from there) rather than a bespoke set of colours here - plus a green for "Assigned",
// the one bucket that palette doesn't already have a colour for.
const CARD_STATUS_COLORS: Record<CardStatusBucket, { bg: string; fg: string }> = {
    pending: { bg: MOBILE_STATUS_PENDING_SOFT, fg: MOBILE_STATUS_PENDING },
    open: { bg: MOBILE_ACCENT_TEAL_SOFT, fg: MOBILE_ACCENT_TEAL },
    assigned: { bg: MOBILE_STATUS_GREEN_SOFT, fg: MOBILE_STATUS_GREEN },
    followingUp: { bg: MOBILE_STATUS_URGENT_SOFT, fg: MOBILE_STATUS_URGENT },
    pendingMatterCreate: { bg: MOBILE_ACCENT_TEAL_SOFT, fg: MOBILE_ACCENT_TEAL },
    converted: { bg: MOBILE_ACCENT_TEAL_SOFT, fg: MOBILE_ACCENT_TEAL },
    lost: { bg: MOBILE_STATUS_URGENT_SOFT, fg: MOBILE_STATUS_URGENT },
    unassigned: { bg: MOBILE_SURFACE_MUTED, fg: MOBILE_TEXT_MUTED },
    other: { bg: MOBILE_SURFACE_MUTED, fg: MOBILE_TEXT_MUTED },
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--dc-surface)',
            // Matches the app's own standard card/table radius - see YourEnquiriesWidget.tsx's
            // identical comment.
            borderRadius: 8,
            padding: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            // A layered "embossed" shadow instead of a hairline border + one faint blur -
            // explicit request ("I want the widgets to pop a bit... doesn't stand out"). Border
            // dropped in favour of the shadow alone carrying the card's own definition, same
            // recipe as every other widget in this panel - see YourEnquiriesWidget.tsx's
            // identical comment.
            boxShadow: '0 1px 3px rgba(16,26,51,0.12), 0 6px 16px rgba(16,26,51,0.08)',
            transition: 'box-shadow 0.15s ease, transform 0.15s ease',
            '&:hover': {
                boxShadow: '0 2px 6px rgba(16,26,51,0.16), 0 10px 28px rgba(16,26,51,0.12)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
            },
        },
        // Gradient wash + oversized watermark icon - explicit follow-up request to bring
        // YourEnquiriesWidget.tsx/YourTasksWidget.tsx/KpiSummaryWidget.tsx's own card
        // background treatment to this widget too, using that same shared blue
        // (SHARED_WASH_ACCENT) rather than each call site's own icon colour - explicit
        // request for one consistent background across every widget.
        wash: {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
        },
        watermark: {
            position: 'absolute',
            top: -20,
            right: -22,
            opacity: 0.08,
            pointerEvents: 'none',
        },
        content: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
        },
        headerRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            marginBottom: theme.spacing(1.5),
        },
        // Explicit request to bring the same kind of animated icon YourEnquiriesWidget.tsx/
        // YourTasksWidget.tsx/KpiSummaryWidget.tsx already have to these three widgets too -
        // the same iconBreathe scale those three share, plus a "ping" ring (a common shared
        // animation across all three "X due" widgets, rather than a bespoke glyph animation
        // per icon shape, since they already share this one component and read as one family).
        // currentColor picks up whatever iconColor is passed at each call site, so the ring
        // matches that widget's own category accent without needing a separate prop for it.
        iconChip: {
            width: 36,
            height: 36,
            minWidth: 36,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            animation: '$iconBreathe 2200ms ease-in-out infinite alternate',
            '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                border: '2px solid currentColor',
                opacity: 0,
                animation: '$iconPing 2000ms ease-out infinite',
            },
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                '&::after': { animation: 'none' },
            },
        },
        '@keyframes iconBreathe': {
            from: { transform: 'scale(1)' },
            to: { transform: 'scale(1.12)' },
        },
        '@keyframes iconPing': {
            '0%': { transform: 'scale(1)', opacity: 0.45 },
            '80%': { transform: 'scale(1.55)', opacity: 0 },
            '100%': { transform: 'scale(1.55)', opacity: 0 },
        },
        heading: {
            fontWeight: 600,
            flex: 1,
        },
        // Bounded height + its own scroll, rather than growing with however many rows have
        // loaded so far - explicit request ("make that table infinite scrollable"). Loading
        // more rows extends what's inside this box instead of growing the drawer's own
        // outer scroll by an unbounded amount. Small symmetric horizontal margin/padding
        // (rather than the old bleed-to-the-card's-edge trick) - overflowY:auto implicitly
        // computes overflowX:auto too (CSS overflow's own "visible + non-visible" rule), so
        // each row's own shadow (see `row` below) needs this breathing room or it gets hard-
        // clipped by this container's own left/right edge instead of fading out naturally.
        rowsScroll: {
            maxHeight: ROW_HEIGHT_PX * VISIBLE_ROWS_BEFORE_SCROLL,
            overflowY: 'auto',
            margin: theme.spacing(0, -0.5),
            padding: theme.spacing(0, 0.5),
        },
        // "card" variant: ONE combined card instead of a stack of separate ones - the scroll box is the white,
        // bordered, rounded card and the rows inside it are separated by hairlines (explicit request, option B).
        rowsScrollCombined: {
            margin: 0,
            padding: 0,
            backgroundColor: 'var(--dc-surface, #FFFFFF)',
            border: '1px solid var(--dc-border, #E8EBF3)',
            borderRadius: 8,
            boxShadow: '0 1px 3px rgba(16,26,51,0.10)',
            scrollbarWidth: 'thin',
        },
        rows: {
            display: 'flex',
            flexDirection: 'column',
            // Real gaps between genuinely separate cards now, not a touching hairline-only
            // list - explicit request ("maybe worth showing them as cards"). Matches the
            // boxed metric-card language YourEnquiriesWidget.tsx/YourTasksWidget.tsx already
            // use elsewhere in this panel, so every widget reads as one consistent "cards
            // inside a card" vocabulary instead of some being plain hover rows.
            gap: theme.spacing(1),
            // A couple of px of slack above/below the first/last card so their own hover
            // lift/shadow isn't clipped by rowsScroll's own overflow:auto boundary.
            padding: theme.spacing(0.25, 0),
        },
        // after `rows` on purpose: same specificity, so the later rule wins and the combined card has no gaps between rows
        rowsCombined: {
            gap: 0,
            padding: 0,
        },
        row: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
            padding: theme.spacing(1.1, 1.25),
            // White-on-white against the widget's own root background, depth carried entirely
            // by the layered shadow below rather than a border - explicit request ("remove the
            // radius of each row, have some embossed or pop effect"). Sharp corners (no
            // border-radius) read as a more structured, table-like edge, while the shadow
            // still lifts each row off the surface behind it.
            backgroundColor: 'var(--dc-surface, #FFFFFF)',
            borderRadius: 0,
            // The left edge is the one rounded-away exception kept sharp on purpose - it's the
            // overdue "flag" (see rowOverdue), not a border for definition, so it stays a flat
            // 3px bar rather than trying to look embossed itself.
            borderLeft: '3px solid transparent',
            boxShadow: '0 1px 2px rgba(16,26,51,0.10), 0 2px 6px rgba(16,26,51,0.06)',
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease, transform 0.15s ease',
            '&:hover': {
                boxShadow: '0 2px 4px rgba(16,26,51,0.14), 0 6px 16px rgba(16,26,51,0.10)',
                transform: 'translateY(-1px)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                transform: 'none !important',
            },
        },
        // Left stripe only - explicit bug report that also washing the whole row in
        // OVERDUE_RED_SOFT (on top of the stripe AND the dueLabel pill's own red tint) was too
        // much red at once, "too contrasting". The stripe plus the pill on the right already
        // flag an overdue row clearly without tinting the entire card.
        rowOverdue: {
            borderLeftColor: OVERDUE_RED,
        },
        // "card" variant (EnquiryFollowUpsDueWidget.tsx) - mobile-app-style row: avatar +
        // name/subtitle + status/assignee + a footer meta line, rather than compact's plain
        // two-line row. Shares rowOverdue's own left-stripe treatment above.
        cardRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            padding: theme.spacing(1.5, 1.75),
            backgroundColor: 'transparent',
            // hairline between rows inside the combined card (none under the last one)
            borderBottom: '1px solid var(--dc-border, #E8EBF3)',
            '&:last-child': {
                borderBottom: 'none',
            },
            cursor: 'pointer',
            transition: 'background-color 0.12s ease',
            '&:hover': {
                backgroundColor: 'rgba(81,115,255,0.06)',
            },
            '&:hover $cardChevron': {
                transform: 'translateX(2px)',
                color: theme.palette.primary.main,
            },
            '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover $cardChevron': { transform: 'none' },
            },
        },
        cardAvatar: {
            width: 34,
            height: 34,
            minWidth: 34,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 800,
            flexShrink: 0,
        },
        cardBody: {
            flex: 1,
            minWidth: 0,
        },
        cardName: {
            fontSize: 14,
            fontWeight: 700,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        cardSubtitle: {
            fontSize: 12.5,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: 1,
        },
        cardMetaRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: theme.spacing(0.75),
            marginTop: 2,
            minWidth: 0,
            overflow: 'hidden',
        },
        // Status and area of law are plain meta text now (status dot + text, a middle dot, then the area), not pills -
        // the due pill on the right is the one coloured pill per row.
        statusPill: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 12,
            fontWeight: 500,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
        },
        statusDot: {
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: 'currentColor',
            flexShrink: 0,
        },
        // Area of law - same icon EnquiryFormDrawer.tsx's own header badge uses (GavelIcon),
        // on the same meta row as the status rather than assignment (explicit request:
        // this widget is already scoped to the signed-in person's own enquiries, so who it's
        // assigned to is redundant here and was just adding height for no new information).
        categoryPill: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: theme.palette.text.secondary,
            '& svg': { fontSize: 12, flexShrink: 0 },
            '$statusPill + &::before': {
                content: '"\\00b7"',
                marginRight: 6,
                color: theme.palette.text.disabled,
            },
        },
        // Right-hand column: the due/overdue pill with "Updated {date}" as a small caption under it.
        cardAside: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 3,
            flexShrink: 0,
        },
        cardFooterMeta: {
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            fontSize: 10.5,
            color: theme.palette.text.disabled,
            whiteSpace: 'nowrap',
            '& svg': { fontSize: 12, flexShrink: 0 },
        },
        cardChevron: {
            flexShrink: 0,
            color: theme.palette.text.disabled,
            transition: 'transform 0.12s ease, color 0.12s ease',
        },
        cardLoadingMoreRow: {
            padding: theme.spacing(1.25, 1.75),
            borderTop: '1px solid var(--dc-border, #E8EBF3)',
        },
        loadingMoreRow: {
            padding: theme.spacing(1, 0, 0, 0),
        },
        rowText: {
            minWidth: 0,
        },
        rowPrimary: {
            fontSize: 13,
            fontWeight: 500,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        rowSecondary: {
            fontSize: 11.5,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        dueLabel: {
            fontSize: 10.5,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            flexShrink: 0,
            borderRadius: 999,
            padding: '3px 8px',
        },
        dueLabelOverdue: {
            color: OVERDUE_RED,
            backgroundColor: OVERDUE_RED_SOFT,
        },
        dueLabelUpcoming: {
            color: theme.palette.text.secondary,
            backgroundColor: 'var(--dc-surface-muted, #F7F8FC)',
        },
        emptyState: {
            fontSize: 13,
            color: theme.palette.text.secondary,
        },
        footer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: theme.spacing(0.25),
            marginTop: theme.spacing(1),
            paddingTop: theme.spacing(1),
            borderTop: '1px solid var(--dc-border)',
            cursor: 'pointer',
            color: theme.palette.primary.main,
            fontSize: 12,
            fontWeight: 600,
            '& svg': {
                transition: 'transform 0.15s ease',
            },
            '&:hover svg': {
                transform: 'translateX(2px)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '& svg': { transition: 'none' },
                '&:hover svg': { transform: 'none' },
            },
        },
        // card variant: "Showing 6 of 12" on the left, "View all 12 ›" on the right
        footerSpread: {
            justifyContent: 'space-between',
        },
        footerCount: {
            fontSize: 12,
            fontWeight: 400,
            color: theme.palette.text.secondary,
        },
        footerLink: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.25),
        },
    })
);

export interface DueListRow {
    key: string;
    primary: string;
    secondary?: string;
    dueLabel: string;
    isOverdue: boolean;
    onClick: () => void;
    // "card" variant only (see DueListWidgetProps.variant) - ignored by the default compact
    // row, so the other two "X due" widgets don't need to supply any of these.
    /** Avatar chip initials, e.g. "AG". */
    initials?: string;
    /** Raw status string - colour is derived from it (getCardStatusBucket above), not passed in. */
    statusLabel?: string | null;
    /** Area of law - shown as an icon pill alongside the status pill. */
    categoryLabel?: string | null;
    /** Already formatted, e.g. "29 Sep 2026" - this component doesn't own date formatting. */
    updatedLabel?: string | null;
}

interface DueListWidgetProps {
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    title: string;
    isLoading: boolean;
    /** Shows a small RefreshingIndicator next to the heading - true only while a Quick View header-button refresh is actually in flight, distinct from isLoading's own initial-load-only skeleton. */
    isRefreshing?: boolean;
    // null = error, or nothing to show yet - the widget itself renders nothing in that case
    // (same "don't assume data is populated" rule as every other widget in this folder).
    rows: DueListRow[] | null;
    totalCount: number;
    emptyMessage: string;
    onViewAll: () => void;
    /** Fetches the next page of rows - omit if the call site has nothing more to page through. */
    onLoadMore?: () => void;
    /** Whether more rows exist beyond what's currently in `rows`. */
    hasMore?: boolean;
    /** Shows a loading row at the bottom of the list while the next page is in flight. */
    isLoadingMore?: boolean;
    /** 'compact' (default) is the original plain two-line row every other "X due" widget still
     * uses; 'card' is the mobile-app-style row (EnquiryFollowUpsDueWidget.tsx/TasksDueWidget.tsx,
     * explicit request to match that reference rather than this drawer's older board-card look). */
    variant?: 'compact' | 'card';
    /** 'card' variant only - the icon shown in each row's categoryLabel pill. Defaults to
     * GavelIcon (area of law) to match EnquiryFollowUpsDueWidget.tsx's own existing look without
     * every call site needing to pass it. */
    categoryIcon?: React.ReactNode;
}

// Shared list chrome for the three "X due" widgets (Enquiry follow-ups due, Contact
// follow-ups due, Tasks due) - same header/row/footer treatment, each call site only
// supplies its own data-fetching and row mapping. Desktop/mouse-oriented (hover rows,
// click-through), not mobile's swipeable card stack - explicit request.
export const DueListWidget: React.FC<DueListWidgetProps> = (props) => {
    const classes = useStyles();

    // A tooltip stays anchored to the row it opened on, so scrolling the list under a hovering mouse carried it away from the
    // widget (floating above the header). While the list scrolls the tooltips are hidden, and when it stops they are reset
    // (new key) so none is left open on a row that has moved - the next hover opens one in the right place.
    const [isListScrolling, setIsListScrolling] = React.useState(false);
    const [tooltipResetKey, setTooltipResetKey] = React.useState(0);
    const scrollEndTimeoutRef = React.useRef<number | null>(null);
    React.useEffect(() => () => {
        if (scrollEndTimeoutRef.current !== null) {
            window.clearTimeout(scrollEndTimeoutRef.current);
        }
    }, []);

    const onRowsScroll = (event: React.UIEvent<HTMLDivElement>) => {
        setIsListScrolling(true);
        if (scrollEndTimeoutRef.current !== null) {
            window.clearTimeout(scrollEndTimeoutRef.current);
        }
        scrollEndTimeoutRef.current = window.setTimeout(() => {
            setIsListScrolling(false);
            setTooltipResetKey((key) => key + 1);
        }, 150);

        if (!props.onLoadMore || !props.hasMore || props.isLoadingMore) {
            return;
        }
        const target = event.currentTarget;
        const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
        if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
            props.onLoadMore();
        }
    };

    if (props.isLoading) {
        return (
            <div className={classes.root}>
                <SkeletonLoader variant="panel" columns={1} rows={4} />
            </div>
        );
    }

    if (props.rows === null) {
        return null;
    }

    // Reuses the exact same icon element the header chip already shows (props.icon), just
    // cloned with an inline fontSize override so it renders oversized/faded as the watermark -
    // same "watermark echoes the header's own icon" recipe YourEnquiriesWidget.tsx/
    // YourTasksWidget.tsx use, so this stays a single source of truth per call site rather
    // than needing a second icon prop. Inline style (not a CSS class targeting the rendered
    // svg) so this reliably wins over whatever fontSize="small" already set at each call site,
    // regardless of MUI's own internal class structure.
    const watermarkIcon = React.isValidElement<{ style?: React.CSSProperties }>(props.icon)
        ? React.cloneElement(props.icon, { style: { fontSize: 112 } })
        : null;

    const isCardVariant = props.variant === 'card';

    return (
        <div className={classes.root}>
            <div className={classes.wash} style={{ background: `linear-gradient(135deg, ${SHARED_WASH_ACCENT}29 0%, ${SHARED_WASH_ACCENT}05 100%)` }} />
            <span className={classes.watermark} style={{ color: SHARED_WASH_ACCENT }}>{watermarkIcon}</span>
            <div className={classes.content}>
                <div className={classes.headerRow}>
                    <span className={classes.iconChip} style={{ backgroundColor: props.iconBg, color: props.iconColor }}>
                        {props.icon}
                    </span>
                    <Typography variant="subtitle2" className={classes.heading}>{props.title}</Typography>
                    {props.isRefreshing && <RefreshingIndicator />}
                </div>
                {props.rows.length > 0 ? (
                    <div
                        className={`${classes.rowsScroll} ${isCardVariant ? classes.rowsScrollCombined : ''}`}
                        style={{ maxHeight: (isCardVariant ? CARD_ROW_HEIGHT_PX : ROW_HEIGHT_PX) * VISIBLE_ROWS_BEFORE_SCROLL }}
                        onScroll={onRowsScroll}
                    >
                        <div className={`${classes.rows} ${isCardVariant ? classes.rowsCombined : ''}`}>
                            {props.rows.map((row) => {
                                if (!isCardVariant) {
                                    return (
                                        <div
                                            key={row.key}
                                            className={`${classes.row} ${row.isOverdue ? classes.rowOverdue : ''}`}
                                            onClick={row.onClick}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { row.onClick(); } }}
                                        >
                                            <div className={classes.rowText}>
                                                <Typography className={classes.rowPrimary}>{row.primary}</Typography>
                                                {row.secondary && <Typography className={classes.rowSecondary}>{row.secondary}</Typography>}
                                            </div>
                                            <Typography className={`${classes.dueLabel} ${row.isOverdue ? classes.dueLabelOverdue : classes.dueLabelUpcoming}`}>
                                                {row.dueLabel}
                                            </Typography>
                                        </div>
                                    );
                                }

                                const statusColors = CARD_STATUS_COLORS[getCardStatusBucket(row.statusLabel)];
                                return (
                                    <div
                                        key={row.key}
                                        className={`${classes.cardRow} ${row.isOverdue ? classes.rowOverdue : ''}`}
                                        onClick={row.onClick}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { row.onClick(); } }}
                                    >
                                        <span className={classes.cardAvatar} style={{ backgroundColor: statusColors.bg, color: statusColors.fg }}>
                                            {row.initials}
                                        </span>
                                        <div className={classes.cardBody}>
                                            <Typography className={classes.cardName}>{row.primary}</Typography>
                                            {row.secondary && (
                                                <Tooltip key={tooltipResetKey} title={isListScrolling ? '' : row.secondary} placement="top" arrow={true}>
                                                    <Typography className={classes.cardSubtitle}>{row.secondary}</Typography>
                                                </Tooltip>
                                            )}
                                            <div className={classes.cardMetaRow}>
                                                {row.statusLabel && (
                                                    <span className={classes.statusPill}>
                                                        <span className={classes.statusDot} style={{ backgroundColor: statusColors.fg }} />
                                                        {row.statusLabel}
                                                    </span>
                                                )}
                                                {row.categoryLabel && (
                                                    <span className={classes.categoryPill}>
                                                        {props.categoryIcon ?? <GavelIcon />}
                                                        {row.categoryLabel}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className={classes.cardAside}>
                                            <Typography className={`${classes.dueLabel} ${row.isOverdue ? classes.dueLabelOverdue : classes.dueLabelUpcoming}`}>
                                                {row.dueLabel}
                                            </Typography>
                                            <span className={classes.cardFooterMeta}>
                                                <EventIcon />
                                                {row.updatedLabel && <span>Updated {row.updatedLabel}</span>}
                                            </span>
                                        </div>
                                        <ChevronRightIcon className={classes.cardChevron} />
                                    </div>
                                );
                            })}
                            {props.isLoadingMore && (
                                <div className={isCardVariant ? classes.cardLoadingMoreRow : classes.loadingMoreRow}>
                                    {/* widths=['100%'] - SkeletonLoader's own "text" variant defaults to a
                                        55%-wide line (built for label/detail text, not a full-width
                                        placeholder), which left this loading-more row looking like a short
                                        stub rather than spanning the same width as the row cards above it. */}
                                    <SkeletonLoader variant="text" rows={1} widths={['100%']} />
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <Typography className={classes.emptyState}>{props.emptyMessage}</Typography>
                )}
                {props.totalCount > 0 && (
                    <div
                        className={`${classes.footer} ${isCardVariant ? classes.footerSpread : ''}`}
                        onClick={props.onViewAll}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') { props.onViewAll(); } }}
                    >
                        {isCardVariant && (
                            <span className={classes.footerCount}>
                                Showing {props.rows.length.toLocaleString()} of {props.totalCount.toLocaleString()}
                            </span>
                        )}
                        <span className={classes.footerLink}>
                            View all {props.totalCount.toLocaleString()}
                            <ChevronRightIcon style={{ fontSize: 16 }} />
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};
